import React, { useState } from "react";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowUpRight, Send } from "lucide-react";
import { C, RESUME_URL, useContent } from "./theme";
import { SectionHead, splitHeading } from "./Chrome";

function Line({ label, testid, ...props }) {
  const Tag = props.rows ? "textarea" : "input";
  return (
    <label className="block">
      <span className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: C.muted }}>{label}</span>
      <Tag data-testid={testid} {...props} className="w-full bg-transparent py-2.5 ed-serif text-xl outline-none resize-none transition-colors border-b focus:border-[#9E3B4B] placeholder:italic placeholder:text-[#9E586280]" style={{ color: C.wine, borderColor: `${C.burgundy}33` }} />
    </label>
  );
}

export function ContactSection() {
  const c = useContent("contact");
  const m = useContent("marquees");
  const email = c.email || "sa9858@nyu.edu";
  const links = [
    ["Email", `mailto:${email}`, "clean-contact-email"],
    ["LinkedIn", c.linkedin || "https://www.linkedin.com/in/shrutiaggarwal8/", "clean-contact-linkedin"],
    ["Medium", c.medium || "https://medium.com/@sa9858", "clean-contact-medium"],
    ["Resume", c.resume || RESUME_URL, "clean-contact-resume"],
    ...(c.instagram ? [["Instagram", c.instagram, "clean-contact-instagram"]] : []),
    ...(c.website ? [[c.websiteLabel || "Website", c.website, "clean-contact-website"]] : []),
  ];
  const [head, ital] = splitHeading(c.heading, "Let's make something", "good.");
  const footer = m.contactFooter || `${email} · NYC-based · I read decks for fun · let's build something obvious in hindsight`;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Fill in everything, please.");
    setBusy(true);
    try {
      await axios.post(api("/api/contact"), form);
      setSent(true);
      toast.success("Got it — I'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send — please email me directly.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="contact" data-testid="clean-contact" className="min-h-screen flex flex-col" style={{ background: C.burgundy }}>
      <div className="flex-1 w-full max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-28 items-center grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-6">
          <SectionHead light num="07" eyebrow={c.eyebrow || "Say hi · RSVP"} heading={head} italic={ital} />
          <p className="-mt-8 text-base md:text-lg leading-relaxed max-w-lg" style={{ color: `${C.cream}cc` }}>
            {c.blurb || "Recruiting for Brand, Strategy, Search, or Product Marketing? Building something wonderful? I'd love to hear about it."}
          </p>
          <ul className="mt-12 max-w-lg">
            {links.map(([label, href, tid]) => (
              <li key={label}>
                <a href={href} target="_blank" rel="noreferrer" data-testid={tid} className="group flex items-center justify-between py-5 transition-colors" style={{ borderTop: `1px solid ${C.cream}26`, color: C.cream }}>
                  <span className="ed-serif text-2xl md:text-3xl font-light transition-transform duration-500 group-hover:translate-x-2">{label}</span>
                  <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" style={{ color: C.blushDeep }} />
                </a>
              </li>
            ))}
          </ul>
        </div>
        <form onSubmit={submit} data-testid="clean-contact-form" className="lg:col-span-6 self-start relative p-8 md:p-12" style={{ background: C.warm, boxShadow: `0 40px 80px ${C.wine}66` }}>
          <span className="absolute inset-3 pointer-events-none" style={{ border: `1px solid ${C.gold}66` }} />
          <p className="ed-mono text-[10px] uppercase tracking-[0.35em] text-center" style={{ color: C.gold }}>The pleasure of your reply</p>
          <p className="ed-serif italic text-3xl md:text-4xl text-center mt-3 mb-10" style={{ color: C.wine }}>is kindly requested</p>
          <div className="space-y-7 relative">
            <Line label="Your name" testid="clean-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ada Lovelace" />
            <Line label="Your email" testid="clean-input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="hello@brand.co" />
            <Line label="A note" testid="clean-input-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="We should talk about…" />
            <button type="submit" disabled={busy} data-testid="clean-submit" className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full ed-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-500 hover:bg-[#9E3B4B]" style={{ background: C.burgundy, color: C.cream }}>
              {busy ? "Sending…" : "Send with love"} <Send size={14} />
            </button>
            {sent && <p data-testid="clean-success" className="ed-serif italic text-center text-lg" style={{ color: C.rose }}>Received — I'll be in touch.</p>}
          </div>
        </form>
      </div>
      <div className="py-8 overflow-hidden" style={{ borderTop: `1px solid ${C.cream}1f` }}>
        <Marquee gradient={false} speed={30} pauseOnHover>
          {footer.split(" · ").map((p, i) => (
            <span key={i} className="ed-serif italic font-light text-4xl md:text-6xl mx-10 flex items-center gap-20" style={{ color: C.blushDeep }}>
              {p} <span className="not-italic text-xl" style={{ color: C.gold }}>✦</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
